/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
  SyntheticEvent,
  useCallback
} from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles
} from '@material-ui/core';
import {useIsPhone} from '../../hooks/useMedia';
import {MaterialExitCloseIcon} from '../../icons/MaterialExitCloseIcon';
import {dlsColor} from '../../theme/theme';
import {DLSTheme} from '../../types/types';
import {HeaderTypography} from '../../typography/HeaderTypography';

interface IonDialogProps {
  onClose: MouseEventHandler;
  title: string;
  actions?: ReactNode;
  stepper?: ReactNode;
  open?: boolean;
  isMaxSize?: boolean;
  shouldCloseOnBackdropClick?: boolean;
  shouldSupportContentHeight?: boolean;
  shouldRemoveDefaultContentPadding?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  paper : {
    borderRadius                   : '10px',
    backgroundColor                : `${dlsColor('grey6')} !important`,
    width                          : ({isMaxSize}: {isMaxSize: boolean}) => isMaxSize? '96vw' : '75vw',
    [theme.breakpoints.down('sm')] : {
      maxWidth : '100%'
    }
  },
  paperScrollPaper : {
    height    : ({isMaxSize}: {isMaxSize: boolean}) => isMaxSize? '96vh' : '75vh',
    maxHeight : ({isMaxSize}: {isMaxSize: boolean}) => isMaxSize? '96vh' : '75vh'
  },
  formDialogTitle : {
    borderBottom : `solid 1px ${dlsColor('grey4')}`
  },
  formTitle : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  closeButton : {
    position  : 'absolute',
    left      : '19px',
    top       : '19px',
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  titleName : {
    textAlign : 'center'
  },
  contentRoot : {
    padding       : '24px',
    display       : 'flex',
    flexDirection : 'column'
  },
  gridContent : {
    padding : '0 24px 24px'
  },
  formActions : {
    borderTop : `solid 1px ${dlsColor('grey4')}`
  },
  content : {
    padding : 0,
    flex    : 'initial'
  }
}));

/**
 * Ion Dialog displays a dialog with the given title.
 * Any passed children are considered the dialog's content.
 * Actions must be passed in to display correctly.
 * 
 * ```typescript
 * 
 *   import {IonDialog} from '@ion/dls/src/components/dialogs/IonDialog';
 * 
 *   <IonDialog onClose={onClose}
 *    open={open}
 *    title={title}
 *    stepper={( // NOTE: including a stepper is optional
 *      <IonStepper activeStep={activeStep} 
 *        onStep={onStep} 
 *        steps={steps} />
 *    )}
 *    actions={(
 *      <IonStepperActionButtons showSecondary
 *        primaryLabel={'Next'}
 *        disableSecondary={disableSecondary}
 *        onPrimary={onPrimary}
 *        onSecondary={onSecondary} />
 *    )}>
 * 
 * ```
 */
export const IonDialog = ({
  title,
  children,
  actions,
  stepper,
  onClose,
  open = false,
  isMaxSize = false,
  shouldCloseOnBackdropClick = false,
  shouldSupportContentHeight = false,
  shouldRemoveDefaultContentPadding = false
}: PropsWithChildren<IonDialogProps>) => {
  const classes = useStyles({isMaxSize});
  const isPhone = useIsPhone();

  const onCloseDialog = useCallback((event: SyntheticEvent<HTMLElement>, reason: string) => {
    if (shouldCloseOnBackdropClick && reason === 'backdropClick') {
      onClose(null);
    }
  }, [shouldCloseOnBackdropClick, onClose]);

  return (
    <Dialog open={open}
      maxWidth={isPhone ? 'sm' : isMaxSize ? false : 'lg'}
      classes={{
        paper : classes.paper,
        ...!shouldSupportContentHeight && {
          paperScrollPaper : classes.paperScrollPaper
        }
      }}
      onClose={onCloseDialog}>
      <DialogTitle classes={{root: classes.formDialogTitle}}>
        <div className={classes.formTitle}>
          <div onClick={onClose} className={classes.closeButton}>
            <MaterialExitCloseIcon color={dlsColor('grey3')} />
          </div>
          <div className={classes.titleName}>
            <HeaderTypography variant='h3'>
              {title}
            </HeaderTypography>
          </div>
        </div>
      </DialogTitle>
      <DialogContent classes={{root: shouldRemoveDefaultContentPadding ? classes.content :classes.contentRoot}}>
        {stepper}
        {children}
      </DialogContent>
      {actions && (
        <DialogActions className={classes.formActions}>
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default IonDialog;
